import {
  ISourceEntityTypes,
  SourceEntityMigrationType,
  type IExpectedSourceRecordSize,
  type IPracticeMigration,
  type ISourceEntity,
} from '@principle-theorem/practice-migrations/interfaces';
import {
  ISO_DATE_FORMAT,
  ISO_DATE_TIME_FORMAT,
  ISODateType,
  toISODate,
  toTimestamp,
  TypeGuard,
  type Timestamp,
  type Timezone,
  type WithRef,
} from '@principle-theorem/shared';
import { flow, groupBy, isNumber, isString } from 'lodash';
import * as moment from 'moment-timezone';
import { BaseSourceEntity } from '../../../source/base-source-entity';
import { runQuery } from '../../../source/connection';
import { SourceEntity } from '../../../source/source-entity';
import { OFFSET_PLACEHOLDER } from '../../../source/source-helpers';
import { cleanObjectStrings } from './lib/conversion-helpers';

export const PATIENT_TREATMENT_NOTE_RESOURCE_TYPE = 'patientTreatmentNote';

export const PATIENT_TREATMENT_NOTE_SOURCE_ENTITY: ISourceEntity =
  SourceEntity.init({
    metadata: {
      label: 'Patient Treatment Note List',
      description: '',
      idPrefix: PATIENT_TREATMENT_NOTE_RESOURCE_TYPE,
      migrationType: SourceEntityMigrationType.Automatic,
    },
  });

export interface ID4WPatientTreatmentNote {
  id: number;
  created_at: string;
  treatment_id: number;
  patient_id: number;
  practice_id: number;
  provider_id: number;
  chart_id: number | null;
  item_id: number;
  item_code: string;
  content: string;
  tooth_ref: string | number | null;
  tooth_surface: string | null;
  treatment_date?: string;
}

export function isD4WPatientTreatmentNote(
  item: unknown,
  logError: boolean
): item is ID4WPatientTreatmentNote {
  return TypeGuard.interface<ID4WPatientTreatmentNote>(
    {
      id: isNumber,
      created_at: isString,
      treatment_id: isNumber,
      patient_id: isNumber,
      practice_id: isNumber,
      provider_id: isNumber,
      chart_id: TypeGuard.nilOr(isNumber),
      item_id: isNumber,
      item_code: isString,
      content: isString,
      tooth_ref: TypeGuard.nilOr([isString, isNumber]),
      tooth_surface: TypeGuard.nilOr(isString),
      treatment_date: TypeGuard.nilOr(isString),
    },
    logError
  )(item);
}

export interface ID4WPatientTreatmentNoteTranslations {
  createdAt: Timestamp;
  treatmentDate?: ISODateType;
}

export interface ID4WPatientTreatmentNoteFilters {
  createdAt: Timestamp;
  treatmentId: string;
  patientId: string;
  practiceId: string;
  chartId?: string;
  userId?: string;
  treatmentDate?: ISODateType;
}

const PATIENT_TREATMENT_NOTE_SOURCE_QUERY = `
SELECT * FROM (
  SELECT
    DISTINCT ON (treat_id)
    treat_notes_log_id AS id,
    id_treat AS treat_id,
    id_treat AS treatment_id,
    datetime1 AS created_at
  FROM
    treat_notes_log
  WHERE ref_status != 'D'
  ORDER BY id_treat ASC, treat_notes_log_id DESC
  ${OFFSET_PLACEHOLDER}
) AS created_table
JOIN (
  SELECT
    DISTINCT ON (notes.treatment_id) id,
    *,
    notes_log.treat_id AS root_treat_id
  FROM (
    SELECT
      treat_id,
      treat_id AS treatment_id,
      is_plan
    FROM treat_notes
    WHERE ref_status != 'D'
    ORDER BY is_plan
  ) AS notes
  INNER JOIN (
    SELECT
      DISTINCT ON (id_treat)
      id_treat AS treat_id,
      treat_notes_log_id AS id,
      datetime1 AS updated_at,
      notes AS content
    FROM treat_notes_log
    WHERE ref_status != 'D'
    ORDER BY id_treat, treat_notes_log_id DESC
  ) AS notes_log
  ON notes.treat_id = notes_log.treat_id
  INNER JOIN (
    SELECT
      treat_id,
      treat_id AS treatment_id,
      patient_id,
      practice_id,
      provider_id,
      chart_id,
      item_id AS original_item_id,
      item_id,
      tooth AS tooth_ref,
      NULLIF(surface, '') AS tooth_surface,
      treat_date AS treatment_date
    FROM treat
  ) AS treatment
  ON notes.treat_id = treatment.treat_id
  INNER JOIN (
    SELECT
      item_id AS original_item_id,
      item_id,
      item AS item_code
    FROM procedures
  ) AS item_codes
  ON treatment.original_item_id = item_codes.original_item_id
  ) AS whole_table
ON created_table.treat_id = whole_table.root_treat_id
  `;

export type D4WPatientTreatmentNote = ISourceEntityTypes<
  ID4WPatientTreatmentNote,
  ID4WPatientTreatmentNoteTranslations,
  ID4WPatientTreatmentNoteFilters
>;

export class PatientTreatmentNoteSourceEntity extends BaseSourceEntity<D4WPatientTreatmentNote> {
  sourceEntity = PATIENT_TREATMENT_NOTE_SOURCE_ENTITY;
  entityResourceType = PATIENT_TREATMENT_NOTE_RESOURCE_TYPE;
  sourceQuery = PATIENT_TREATMENT_NOTE_SOURCE_QUERY;
  allowOffsetJob = true;
  verifySourceFn = isD4WPatientTreatmentNote;
  override defaultOffsetSize = 10000;

  override transformDataFn = flow([
    (rows: ID4WPatientTreatmentNote[]) => rows.map(cleanObjectStrings),
  ]);

  translate(
    data: ID4WPatientTreatmentNote,
    timezone: Timezone
  ): ID4WPatientTreatmentNoteTranslations {
    return {
      createdAt: toTimestamp(
        moment.tz(data.created_at, ISO_DATE_TIME_FORMAT, timezone)
      ),
      treatmentDate: data.treatment_date
        ? toISODate(moment.tz(data.treatment_date, ISO_DATE_FORMAT, timezone))
        : undefined,
    };
  }

  override async getExpectedRecordSize(
    migration: WithRef<IPracticeMigration>
  ): Promise<IExpectedSourceRecordSize> {
    const response = await runQuery<{ id: number }>(
      migration,
      PATIENT_TREATMENT_NOTE_SOURCE_QUERY.replace(OFFSET_PLACEHOLDER, '')
    );
    const expectedSize = Object.values(
      groupBy(response.rows, (treatmentNote) => treatmentNote.id)
    ).length;

    return {
      expectedSize,
      expectedSizeCalculatedAt: toTimestamp(),
    };
  }

  getSourceRecordId(data: ID4WPatientTreatmentNote): number {
    return data.treatment_id;
  }

  getSourceLabel(data: ID4WPatientTreatmentNote): string {
    return `${data.treatment_id}`;
  }

  getFilterData(
    data: ID4WPatientTreatmentNote,
    timezone: Timezone
  ): ID4WPatientTreatmentNoteFilters {
    return {
      treatmentId: data.treatment_id.toString(),
      patientId: data.patient_id.toString(),
      practiceId: data.practice_id.toString(),
      chartId: data.chart_id?.toString(),
      createdAt: toTimestamp(
        moment.tz(data.created_at, ISO_DATE_TIME_FORMAT, timezone)
      ),
      treatmentDate: data.treatment_date
        ? toISODate(moment.tz(data.treatment_date, ISO_DATE_FORMAT, timezone))
        : undefined,
    };
  }
}
